// narrateKidStep — turn a parent's cook-step transition into a
// pet-voiced sentence the kid hears on their tablet. Composes
// pre-baked bundled clips with the per-canonical voiceline so the
// pet can say:
//   "Your"  +  "EGG BURGER!"  +  "is in the oven!"
//
// Classification is keyword-matching on the step title (cheap, no
// AI). Falls back to a generic "is cooking!" if no specific keyword
// matches. Missing bundled clips skip silently so a partial library
// still narrates the parts it has.

import { playKidVoiceSequence } from "./kidVoice";
import { getCanonicalVoicelineUrl } from "./canonicalVoicelines";

// Step-title keyword → { singular, plural } suffix clip keys.
// Order matters — first match wins so "preheat oven" matches
// "is-heating-up" before generic "is-in-the-oven". Plural variants
// are optional; when missing we fall back to the singular clip.
const STEP_KEYWORDS = [
  [/preheat|warm\s*up|heat\s*up|bring\s*to\s*temp/i, { sg: "is-heating-up" }],
  [/almost\s*done|nearly\s*done|final\s*minute/i,    { sg: "is-almost-done" }],
  [/ready|finished|done|plate|serve/i,                { sg: "is-ready" }],
  [/cool|rest|let\s*sit/i,                            { sg: "is-cooling" }],
  [/flip|turn\s*over/i,                               { sg: "is-flipping" }],
  [/mix|stir|whisk|fold|combine|toss/i,               { sg: "is-stirring" }],
  [/cut|chop|slice|dice|mince|crush/i,                { sg: "is-cutting" }],
  [/sizzle|sear|sauté|saute|pan[- ]?fry/i,            { sg: "is-sizzling" }],
  [/bake|roast/i,                                      { sg: "is-baking" }],
  [/oven|broil/i,                                      { sg: "is-in-the-oven", pl: "are-in-the-oven" }],
];

// Plural-food heuristic. "fries", "nuggets", "wings", "tacos",
// "cookies" → plural ("Are in the oven!"). "eggwich", "burger",
// "cheese" → singular ("Is in the oven!"). Word ends in 's' but
// not 'ss' / 'us' / 'is' / 'es' AND not in a known-singular short
// list. Cheap, wrong on edge cases (kid_label can override later
// via a per-food plural flag if needed).
const SINGULAR_S_WORDS = new Set([
  "rice", "couscous", "hummus", "asparagus", "cactus", "mousse",
  "bouillabaisse", "swiss",
]);
function isPluralFood(name) {
  const last = String(name || "").trim().toLowerCase().split(/\s+/).pop() || "";
  if (!last) return false;
  if (SINGULAR_S_WORDS.has(last)) return false;
  if (/(?:ss|us)$/.test(last)) return false;
  return /s$/.test(last);
}

function classifyStep(stepTitle, foodName) {
  const t = String(stepTitle || "").toLowerCase();
  for (const [pattern, keys] of STEP_KEYWORDS) {
    if (pattern.test(t)) {
      const plural = isPluralFood(foodName);
      return (plural && keys.pl) ? keys.pl : keys.sg;
    }
  }
  return null;
}

// Narrate a step change for a kid. Best-effort: silent if the pet
// has no voice_id or no canonical_voicelines row for the slug.
//
//   foodSlug    — canonical_id (for pantry items) or recipe slug
//   targetKind  — "canonical" | "recipe"
//   petSlug     — kid's active pet (drives voice_id lookup)
//   stepTitle   — the step row title we just observed
export async function narrateKidStep({
  foodSlug,
  foodName,         // optional — drives singular/plural picker; falls
                    // back to foodSlug when not supplied
  targetKind = "canonical",
  petSlug,
  stepTitle,
}) {
  if (!foodSlug || !petSlug) return;
  const suffixKey = classifyStep(stepTitle, foodName || foodSlug);
  if (!suffixKey) return; // ambiguous step — don't make stuff up
  const foodUrl = await getCanonicalVoicelineUrl(foodSlug, petSlug, targetKind);
  if (!foodUrl) {
    // No per-food voiceline yet; play the generic sentence without
    // the name. Still better than silence.
    await playKidVoiceSequence(["your", suffixKey]);
    return;
  }
  await playKidVoiceSequence([
    "your",
    { url: foodUrl },
    suffixKey,
  ]);
}

// Exposed for unit-test parity / debugging.
export { classifyStep };
